import { ViewOnlyRestrictionWhen } from '@/components/custom-roles';
import { BrowseViewCoursesRow, useBrowseViewCourses } from '@/hooks/query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { BrowseViewCourseCard } from './BrowseViewCourseCard';
import { CourseScrollRow } from './CourseScrollRow';
import { CreateNewContentCard } from './CreateNewContentCard';

const BrowseViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 46px;
`;

const RowGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--small-padding);
`;

const RowNote = styled.p`
  margin: 0;
  color: var(--gray-darkest);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
`;

type BrowseRowProps = {
  title: string;
  emptyMessage?: string;
  row: BrowseViewCoursesRow;
  onCourseSelect: (courseId: string) => void;
  leadCard?: React.ReactNode;
};

const BrowseRow: React.FC<BrowseRowProps> = ({
  title,
  emptyMessage,
  row,
  onCourseSelect,
  leadCard,
}) => {
  const { t } = useTranslation();

  const hasContent = !!row.courses?.length || !!leadCard;
  const loadErrorMessage = t('content.browse.loadError');

  const getNote = () => {
    if (row.isError) return hasContent ? loadErrorMessage : undefined;

    if (row.isTruncated)
      return t('content.browse.truncated', {
        limit: row.courses?.length ?? 0,
      });

    return undefined;
  };

  const note = getNote();

  const scrollRow = (
    <CourseScrollRow
      title={title}
      emptyMessage={row.isError ? loadErrorMessage : emptyMessage}
      isLoading={row.isLoading}
      isFetching={row.isFetching}
      leadCard={leadCard}
    >
      {row.courses?.map((course) => (
        <BrowseViewCourseCard
          key={course.id}
          course={course}
          onSelect={onCourseSelect}
        />
      ))}
    </CourseScrollRow>
  );

  if (!note) return scrollRow;

  return (
    <RowGroup>
      {scrollRow}
      <RowNote role="status">{note}</RowNote>
    </RowGroup>
  );
};

export type ContentBrowseViewProps = {
  accountId?: string;
  onCourseSelect: (courseId: string) => void;
  onCreateNewContent: () => void;
  canCreateContent: boolean;
  isCreateRestricted?: boolean;
};

export const ContentBrowseView: React.FC<ContentBrowseViewProps> = ({
  accountId,
  onCourseSelect,
  onCreateNewContent,
  canCreateContent,
  isCreateRestricted = false,
}) => {
  const { t } = useTranslation();
  const { cyberPilotContent, newAndUpcoming, customContent } =
    useBrowseViewCourses({ accountId });

  return (
    <BrowseViewContainer className="content-full-bleed">
      <BrowseRow
        title={t('content.browse.cyberPilotContent')}
        emptyMessage={t('content.browse.empty.cyberPilotContent')}
        row={cyberPilotContent}
        onCourseSelect={onCourseSelect}
      />
      <BrowseRow
        title={t('content.browse.newAndUpcoming')}
        emptyMessage={t('content.browse.empty.newAndUpcoming')}
        row={newAndUpcoming}
        onCourseSelect={onCourseSelect}
      />
      <BrowseRow
        title={t('content.browse.customContent')}
        row={customContent}
        onCourseSelect={onCourseSelect}
        leadCard={
          <ViewOnlyRestrictionWhen restricted={isCreateRestricted}>
            <CreateNewContentCard
              onClick={onCreateNewContent}
              disabled={!canCreateContent}
            />
          </ViewOnlyRestrictionWhen>
        }
      />
    </BrowseViewContainer>
  );
};
